import React, { useMemo, useState, useCallback } from 'react'
import { Calendar, Panel, Popover, Whisper, Button, CheckPicker } from 'rsuite'
import SelectAssemblerPicker from '@components/common/commonFormInput/SelectAssemblerPicker'
import { makeBetweenFilter, makeFilter } from '@@partner:helpers/crud/filters'
import type { GQL_ID } from '@@partner:types:graphql'
import './AssemblingShiftsCalendar.scss'
import { WorkShift } from '@@partner:types:app'
import { useFetchAllWorkShifts } from '@@partner:graphql/query/assemblerWorkShifts/allWorkShifts'
import { formatWorkshiftStatusLabel, SHIFT_STATUSES } from '@@partner:helpers/formatters/workshift'
import { ShopsPicker } from '@components/common/pickers'
import { WORKSHIFT } from '@hive/status'

interface AssemblingShiftsCalendarProps {
  onDateClick?: (date: Date) => void
  onEditShift?: (shift: WorkShift) => void
  onViewShift?: (shift: WorkShift) => void
}

export default function AssemblingShiftsCalendar ({
  onDateClick,
  onEditShift,
  onViewShift
}: AssemblingShiftsCalendarProps) {
  // Состояние для отслеживания текущего месяца календаря
  const [ currentDate, setCurrentDate ] = useState(new Date())
  
  // Состояние фильтров календаря
  const [ calendarFilterState, setCalendarFilterState ] = useState<{
    assemblerIds: GQL_ID[]
    shopIds: GQL_ID[]
    status: number[]
  }>({
    assemblerIds: [],
    shopIds: [],
    status: []
  })

  // Вычисляем диапазон дат для календаря
  const calendarDateRange = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    
    const firstDayOfMonth = new Date(year, month, 1)
    const firstDayOfWeek = firstDayOfMonth.getDay()
    const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1
    const calendarStartDate = new Date(firstDayOfMonth)
    calendarStartDate.setDate(firstDayOfMonth.getDate() - daysToSubtract)
    
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const lastDayOfWeek = lastDayOfMonth.getDay()
    const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek
    const calendarEndDate = new Date(lastDayOfMonth)
    calendarEndDate.setDate(lastDayOfMonth.getDate() + daysToAdd)
    
    return {
      startDate: calendarStartDate,
      endDate: calendarEndDate
    }
  }, [ currentDate ])

  // Создаем фильтр для календаря
  const calendarFilters = useMemo(() => {
    const { startDate, endDate } = calendarDateRange
    
    const startTimestamp = new Date(startDate)
    startTimestamp.setHours(0, 0, 0, 0)
    
    const endTimestamp = new Date(endDate)
    endTimestamp.setHours(23, 59, 59, 999)
    
    const filters = []
    
    filters.push(
      makeBetweenFilter('ts_starting', {
        from: startTimestamp.getTime(),
        to: endTimestamp.getTime()
      })
    )
    
    if (calendarFilterState.assemblerIds.length > 0) {
      filters.push(makeFilter('assembler_id', 'contains', calendarFilterState.assemblerIds))
    }
    if (calendarFilterState.shopIds.length > 0) {
      filters.push(makeFilter('assemblerShopId', 'contains', calendarFilterState.shopIds))
    }
    if (calendarFilterState.status.length > 0) {
      filters.push(makeFilter('status', 'contains', calendarFilterState.status))
    }

    return {
      and: filters
    }
  }, [ calendarDateRange, calendarFilterState ])

  const { workshifts: shifts, loadingWorkShifts, errorWorkShifts } = useFetchAllWorkShifts({
    filter: calendarFilters,
    pager: { page: 1, limit: 1000 },
    sort: {
      direction: 0,
      fieldName: 'ts_starting'
    }
  })

  // Группируем смены по датам
  const shiftsByDate = useMemo(() => {
    const groups: Record<string, WorkShift[]> = {}
    
    shifts.forEach(shift => {
      if (shift.plannedStartAt) {
        const date = new Date(shift.plannedStartAt)
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const dateKey = `${year}-${month}-${day}`
        
        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        groups[dateKey].push(shift)
      }
    })
    
    return groups
  }, [ shifts ])

  // Генерация цвета для сборщика
  const getAssemblerColor = (assemblerId: string | number): string => {
    const colors = [
      '#1976d2', '#d32f2f', '#388e3c', '#f57c00', 
      '#7b1fa2', '#5d4037', '#c2185b', '#00796b',
      '#303f9f', '#689f38', '#e64a19', '#455a64'
    ]
    const index = (typeof assemblerId === 'string' ? parseInt(assemblerId, 10) : assemblerId) % colors.length
    return colors[index]
  }

  // Форматирование времени смены
  const formatShiftTime = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`
    const endTime = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`
    
    return `${startTime}-${endTime}`
  }

  // Рендер ячейки календаря
  const renderCell = (date: Date) => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const dateKey = `${year}-${month}-${day}`
    
    const shiftsForDate = shiftsByDate[dateKey] || []
    
    if (shiftsForDate.length === 0) {
      return null
    }

    const displayedShifts = shiftsForDate.slice(0, 6)
    const hasMore = shiftsForDate.length > 6

    return (
      <div className="shifts-calendar-cell">
        {displayedShifts.map((shift) => {
          const statusConfig = formatWorkshiftStatusLabel(shift.status)
          const timeText = formatShiftTime(shift.plannedStartAt, shift.plannedFinishAt)
          const assemblerName = shift.assembler ? `${shift.assembler.first_name} ${shift.assembler.last_name}` : 'Не назначен'
          const assemblerColor = shift.assembler ? getAssemblerColor(shift.assembler.id) : '#666666'
          
          const shiftTooltip = (
            <div>
              <div><strong>{assemblerName}</strong></div>
              <div>Время: {timeText}</div>
              <div>Статус: {statusConfig?.label || `Неизвестно (${shift.status})`}</div>
            </div>
          )

          return (
            <div className='shift-badge-hover-container'>
              <Whisper
                key={shift.id}
                trigger="hover"
                speaker={<Popover>{shiftTooltip}</Popover>}
              >
                <div
                  className="shift-badge-wrapper"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (onViewShift) {
                      onViewShift(shift)
                    }
                    else {
                      onEditShift?.(shift)
                    }
                  }}
                  style={{ borderColor: assemblerColor }}
                >
                  <div className="shift-badge-content">
                    <div className="first-line">
                      <div className="assembler-name">{assemblerName}</div>
                      <div className="shift-time">{timeText}</div>
                    </div>
                  </div>
                </div>
              </Whisper>
            </div>
          )
        })}
        {hasMore && (
          <div className="more-badge">
            +{shiftsForDate.length - 6}
          </div>
        )}
      </div>
    )
  }

  const handleDateSelect = (date: Date) => {
    onDateClick?.(date)
  }

  // Обработчик изменения месяца в календаре
  const handleCalendarChange = (date: Date) => {
    setCurrentDate(date)
  }

  // Обработчики фильтров
  const handleAssemblerFilterChange = useCallback((assemblerIds: GQL_ID[]) => {
    setCalendarFilterState(prev => ({ ...prev, assemblerIds: assemblerIds || [] }))
  }, [])

  const handleResetFilters = useCallback(() => {
    setCalendarFilterState({
      assemblerIds: [],
      shopIds: [],
      status: []
    })
  }, [])

  if (errorWorkShifts) {
    return (
      <Panel header="Календарь смен" bordered>
        <div className="error-message">
          Ошибка загрузки календаря: {errorWorkShifts.message}
        </div>
      </Panel>
    )
  }

  return (
    <Panel header={
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {loadingWorkShifts && (
          <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
            Обновление...
          </span>
        )}
      </div>
    } bordered className="assembler-shifts-calendar">
      {/* Фильтры календаря */}
      <div className="calendar-filters" style={{ 
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#495057' }}>Фильтры:</span>

          <ShopsPicker
            multiple
            value={calendarFilterState.shopIds}
            onChange={(value) => setCalendarFilterState(prev => ({ ...prev, shopIds: value || undefined }))}
          />
          <SelectAssemblerPicker
            isMultiple={true}
            value={calendarFilterState.assemblerIds}
            onChange={handleAssemblerFilterChange}
          />
          <CheckPicker
            data={SHIFT_STATUSES}
            value={calendarFilterState.status}
            onChange={(value) => setCalendarFilterState(prev => ({
              ...prev,
              status: value
            }))}
            placeholder={'Статус смены'}
            valueKey='value'
            labelKey='label'
            size={'sm'}
          />
        </div>

        {calendarFilterState.assemblerIds.length > 0 && (
          <Button
            onClick={handleResetFilters}
            size="sm"
            appearance="subtle"
            color="red"
            startIcon={<span>×</span>}
          >
            Сбросить
          </Button>
        )}
      </div>

      {loadingWorkShifts && shifts.length === 0 && (
        <div className="loading-message" style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '10px 20px',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}>
          Загрузка календаря...
        </div>
      )}

      <Calendar
        locale={{
          sunday: 'Вс',
          monday: 'Пн', 
          tuesday: 'Вт',
          wednesday: 'Ср',
          thursday: 'Чт',
          friday: 'Пт',
          saturday: 'Сб',
          ok: 'ОК',
          today: 'Сегодня',
          yesterday: 'Вчера'
        }}
        renderCell={renderCell}
        onSelect={handleDateSelect}
        onChange={handleCalendarChange}
        isoWeek
      />
      
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-info">
            <strong>Цветовое кодирование:</strong> Каждый сборщик имеет свой уникальный цвет
          </div>
        </div>
      </div>
    </Panel>
  )
}